import { useState, useEffect } from 'react';
import { getSalesRange } from '../utils/api';
import PeriodPicker from '../components/PeriodPicker';
import { useNicknames } from '../utils/people';
import ExportButton from '../components/ExportButton';
import { formatCurrency, formatDateShort, getStatusBadgeClass } from '../utils/format';

/* Sales Report — every job sold in the chosen month / quarter / year / custom range (by sold date).
   Revenue = Change Order total when there is one, otherwise the Contract Amount.
   Totals and rep cards leave cancelled jobs out; cancelled jobs are listed at the bottom in red. */

const PRODUCT_COLORS = {
  roof:       '#C6E0B4',
  coating:    '#ADD8E6',
  windows:    '#FF5050',
  insulation: '#CC66FF',
  gutters:    '#FFE0B2',
  patio:      '#FFF9C4',
  fencing:    '#F8BBD0',
};

function getProductColor(service) {
  if (!service) return '#CC66FF';
  const s = service.toLowerCase();
  for (const [key, color] of Object.entries(PRODUCT_COLORS)) {
    if (s.includes(key)) return color;
  }
  return '#CC66FF';
}

export default function SalesReport() {
  const nick = useNicknames();
  const [data, setData]   = useState(null);
  const [period, setPeriod] = useState(null);   // { from, to, label }
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!period) return;
    setLoading(true);
    getSalesRange(period.from, period.to).then(setData).finally(() => setLoading(false));
  }, [period]);

  const allJobs   = data?.jobs || [];   // sold first, cancelled at the bottom (server order)
  const t         = data?.totals;
  const monthLabel = period?.label || '';
  const year = (period?.from || '').slice(0, 4);

  const PrintIcon = () => (
    <svg viewBox="0 0 20 20" fill="currentColor" style={{width:15,height:15}}>
      <path fillRule="evenodd" d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a1 1 0 001 1h8a1 1 0 001-1v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a1 1 0 00-1-1H6a1 1 0 00-1 1zm2 0h6v3H7V4zm-1 9h8v4H6v-4zm-2-4a1 1 0 100 2 1 1 0 000-2z" clipRule="evenodd"/>
    </svg>
  );

  return (
    <>
      <div className="page-header">
        <h2>Sales Report</h2>
        <div className="page-header-actions">
          <button className="btn btn-outline btn-sm" onClick={() => window.print()}>
            <PrintIcon /> Print
          </button>
          <ExportButton sheet='sales' year={year} />
        </div>
      </div>

      <div className="page-body">
        <div className="screen-only" style={{ marginBottom: 16 }}><PeriodPicker onChange={setPeriod} allowCustom /></div>
        {loading ? (
          <div className="loader"><div className="spinner" /></div>
        ) : !data ? null : (
          <>
            {/* ── SCREEN VIEW ── */}
            <div className="screen-only">
              {/* Summary cards */}
              <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
                <div className="stat-card" style={{ minWidth:170 }}>
                  <div className="stat-label">Total Jobs Sold</div>
                  <div className="stat-value">{t.sold.jobs}</div>
                  <div className="stat-sub">{formatCurrency(t.sold.revenue)}</div>
                  <div className="stat-sub" style={{ fontSize:'0.7rem', opacity:.75 }}>cancelled not included</div>
                </div>
                <div className="stat-card" style={{ minWidth:150, borderColor:'#F5B7B1' }}>
                  <div className="stat-label" style={{ color:'#B23B2E' }}>Cancelled</div>
                  <div className="stat-value" style={{ color:'#B23B2E' }}>{t.cancelled.jobs}</div>
                  <div className="stat-sub">{formatCurrency(t.cancelled.revenue)}</div>
                </div>
                {data.repSummary && data.repSummary.map(rep => (
                  <div key={rep.sales_rep} className="stat-card gold" style={{ minWidth:140 }}>
                    <div className="stat-label">{rep.sales_rep ? nick(rep.sales_rep) : 'No rep entered'}</div>
                    <div className="stat-value" style={{ fontSize:'1.5rem' }}>{rep.total_jobs}</div>
                    <div className="stat-sub">{formatCurrency(rep.total_value)}</div>
                  </div>
                ))}
              </div>

              {/* Job list table */}
              <div className="card">
                <div className="card-header">
                  <h3>{monthLabel} — Sales Report</h3>
                </div>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>PO #</th>
                        <th>Date Sold</th>
                        <th>Customer Name</th>
                        <th>Sales Rep</th>
                        <th>Service Type</th>
                        <th>Sales Status</th>
                        <th>Sale Amount</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allJobs.length === 0 ? (
                        <tr><td colSpan={8} style={{ textAlign:'center', padding:24, color:'#aaa' }}>No jobs sold in {monthLabel}</td></tr>
                      ) : allJobs.map((job, idx) => {
                        const isCancelled = job.cancelled;
                        return (
                          <tr key={job.po_number} style={{ background: isCancelled ? '#FFC7C7' : idx % 2 === 0 ? '#E8F5E9' : '#fff' }}>
                            <td className="td-po">{job.po_number}</td>
                            <td>{formatDateShort(job.contract_date)}</td>
                            <td className="td-name">{job.customer_name}</td>
                            <td>{nick(job.sales_rep)}{job.sales_rep_2 ? ` / ${nick(job.sales_rep_2)}` : ''}</td>
                            <td><span style={{ background: getProductColor(job.service), padding:'2px 8px', borderRadius:6, fontSize:'0.8rem', fontWeight:600 }}>{job.services.join(', ') || '—'}</span></td>
                            <td><span className={getStatusBadgeClass(job.sales_status)}>{job.sales_status}</span></td>
                            <td className="td-amt">{formatCurrency(job.revenue)}</td>
                            <td style={{ fontSize:'0.78rem', color:'var(--gray-500)' }}>{job.notes}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* ── PRINT VIEW ── */}
            <div className="print-only">
              <div style={{ marginBottom:8, borderBottom:'2px solid #0D3B2E', paddingBottom:4, display:'flex', justifyContent:'space-between', alignItems:'flex-end' }}>
                <h2 style={{ fontFamily:'Georgia,serif', color:'#0D3B2E', fontSize:'14px', margin:0 }}>CPR4homes — {monthLabel} Sales Report</h2>
                <p style={{ fontSize:'8px', color:'#555', margin:0 }}>Printed: {new Date().toLocaleDateString()}</p>
              </div>

              <div style={{ display:'flex', gap:16, marginBottom:8, fontSize:'9px' }}>
                <span style={{ background:'#F57F17', color:'#fff', padding:'2px 8px', borderRadius:3, fontWeight:700 }}>Jobs Sold: {t.sold.jobs}</span>
                <span style={{ background:'#F57F17', color:'#fff', padding:'2px 8px', borderRadius:3, fontWeight:700 }}>Revenue: {formatCurrency(t.sold.revenue)}</span>
                <span style={{ padding:'2px 8px', borderRadius:3, border:'1px solid #ccc' }}>Cancelled: {t.cancelled.jobs} — {formatCurrency(t.cancelled.revenue)}</span>
              </div>

              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'8px' }}>
                <thead>
                  <tr style={{ background:'#1B5E20', color:'#fff', WebkitPrintColorAdjust:'exact', printColorAdjust:'exact' }}>
                    <th style={{ padding:'4px 5px', textAlign:'left', whiteSpace:'nowrap' }}>PO #</th>
                    <th style={{ padding:'4px 5px', textAlign:'left', whiteSpace:'nowrap' }}>Date Sold</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Customer Name</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Sales Rep</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Service</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Status</th>
                    <th style={{ padding:'4px 5px', textAlign:'right', whiteSpace:'nowrap' }}>Sale Amount</th>
                    <th style={{ padding:'4px 5px', textAlign:'left' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {allJobs.map((job, idx) => {
                    const isCancelled = job.cancelled;
                    const bg = isCancelled ? '#FFC7C7' : idx % 2 === 0 ? '#E8F5E9' : '#ffffff';
                    return (
                      <tr key={job.po_number} style={{ background:bg, WebkitPrintColorAdjust:'exact', printColorAdjust:'exact', pageBreakInside:'avoid' }}>
                        <td style={{ padding:'3px 5px', fontWeight:700, borderBottom:'1px solid #ddd', whiteSpace:'nowrap' }}>{job.po_number}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd', whiteSpace:'nowrap' }}>{formatDateShort(job.contract_date)}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd' }}>{job.customer_name}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd' }}>{nick(job.sales_rep)}{job.sales_rep_2 ? ` / ${nick(job.sales_rep_2)}` : ''}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd' }}>{job.services.join(', ')}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd' }}>{job.sales_status}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd', textAlign:'right', whiteSpace:'nowrap' }}>{formatCurrency(job.revenue)}</td>
                        <td style={{ padding:'3px 5px', borderBottom:'1px solid #ddd', fontSize:'7px', color:'#555' }}>{job.notes}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </>
  );
}
